import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, Eye, Mail, Heart, Edit, Trash2, Upload } from 'lucide-react';
import { formatCurrency, getStatusBadgeClasses } from '../../constants/propertyConstants';

/**
 * PropertyCard Component
 * Displays a property in a card format with quick actions
 */
const PropertyCard = ({ property, onEdit, onDelete, onPublish }) => {
    const navigate = useNavigate();

    const handleClick = (e) => {
        // Don't navigate if clicking on action buttons
        if (e.target.closest('button')) {
            return;
        }
        navigate(`/landlord/properties/${property.propertyId}`);
    };

    return (
        <div
            className="bg-white rounded-lg shadow-sm border border-soil-100 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={handleClick}
        >
            {/* Image */}
            <div className="relative h-48 bg-soil-100">
                {(property.images && property.images.length > 0) || property.featuredImageUrl || property.propertyImage ? (
                    <img
                        src={property.images?.[0] || property.featuredImageUrl || property.propertyImage}
                        alt={property.propertyName}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-soil-400">
                        <Square className="h-16 w-16" />
                    </div>
                )}

                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClasses(property.status || property.listing_status)}`}>
                        {(property.status || property.listing_status || 'draft').toUpperCase()}
                    </span>
                </div>

                {/* Image Count */}
                {property.totalImages > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                        {property.totalImages} photos
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-4">
                {/* Title */}
                <h3 className="font-display text-lg font-semibold text-soil-900 mb-2 line-clamp-1">
                    {property.propertyName || property.property_name}
                </h3>

                {/* Location */}
                <div className="flex items-center text-sm text-soil-600 mb-3">
                    <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="line-clamp-1">
                        {property.city || property.location}
                        {property.neighborhood && `, ${property.neighborhood}`}
                    </span>
                </div>

                {/* Price */}
                <div className="mb-3">
                    <p className="font-mono text-2xl font-bold text-highland-500">
                        {formatCurrency(property.pricePerMonth || property.rent_amount, property.currency || 'KES')}
                        <span className="text-sm font-normal text-soil-600">/month</span>
                    </p>
                </div>

                {/* Property Details */}
                <div className="flex items-center space-x-4 text-sm text-soil-600 mb-4">
                    {(property.bedrooms || property.total_bedrooms) !== null && (
                        <div className="flex items-center">
                            <Bed className="h-4 w-4 mr-1" />
                            {property.bedrooms || property.total_bedrooms || 0}
                        </div>
                    )}
                    {(property.bathrooms || property.total_bathrooms) !== null && (
                        <div className="flex items-center">
                            <Bath className="h-4 w-4 mr-1" />
                            {property.bathrooms || property.total_bathrooms || 0}
                        </div>
                    )}
                    {(property.squareFeet || property.total_area_sqft) && (
                        <div className="flex items-center">
                            <Square className="h-4 w-4 mr-1" />
                            {property.squareFeet || property.total_area_sqft} ft²
                        </div>
                    )}
                </div>

                {/* Statistics */}
                <div className="flex items-center space-x-4 text-xs text-soil-400 mb-4 pb-4 border-b border-soil-100">
                    <div className="flex items-center">
                        <Eye className="h-3 w-3 mr-1" />
                        {property.viewCount || property.views_count || 0} views
                    </div>
                    <div className="flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {property.inquiryCount || property.inquiries_count || 0} inquiries
                    </div>
                    <div className="flex items-center">
                        <Heart className="h-3 w-3 mr-1" />
                        {property.favoriteCount || property.saves_count || 0} saves
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => onEdit(property.propertyId || property.property_id)}
                        className="flex-1 flex items-center justify-center px-3 py-2 bg-highland-500 text-white text-sm font-medium rounded-lg hover:bg-highland-600 transition-colors"
                        title="Edit Property"
                    >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                    </button>

                    {(property.status === 'draft' || property.listing_status === 'draft') && (
                        <button
                            onClick={() => onPublish(property.propertyId || property.property_id)}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-dawn-500 text-soil-900 text-sm font-medium rounded-lg hover:bg-dawn-600 transition-colors"
                            title="Publish Property"
                        >
                            <Upload className="h-4 w-4 mr-1" />
                            Publish
                        </button>
                    )}

                    <button
                        onClick={() => onDelete(property.propertyId || property.property_id)}
                        className="px-3 py-2 bg-murram-500 text-white rounded-lg hover:bg-murram-600 transition-colors"
                        title="Delete Property"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertyCard;
